import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Field, FieldProps, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Button, FieldWrapper, InputIconWrapper } from 'components';
import { appConfig } from 'config/app';
import { theme } from 'design';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { Site, updateCreditCardFeeRequest } from 'store/Site';
import { IStore } from 'store/types';
import { ISite } from 'types';
import { ActionSection } from '../../../styles';
import { NumberInput } from '../../shared/styles';

const { TIP_CALCULATOR } = appConfig;

interface ICreditCardFeesFormProps {
  site: ISite;
  onClose(): void;
}

// Allow at most 3 integer digits and 2 decimals (blocks typing/paste beyond that)
const feeInputPattern = /^\d{0,3}(\.\d{0,2})?$/i;

const validationSchema = Yup.object().shape({
  percentFee: Yup.string()
    .required('Required')
    .test('range', `Invalid percentage. Enter a value from ${TIP_CALCULATOR.MIN_PERCENT_FEE_VALUE} to ${TIP_CALCULATOR.MAX_PERCENT_FEE_VALUE}.`, (value) => {
      const parsed = Number(value);
      return !Number.isNaN(parsed) && parsed >= TIP_CALCULATOR.MIN_PERCENT_FEE_VALUE && parsed <= TIP_CALCULATOR.MAX_PERCENT_FEE_VALUE;
    }),
});

export const CreditCardFeesForm: React.FC<ICreditCardFeesFormProps> = ({ site, onClose }) => {
  const dispatch = useDispatch();
  const isUpdateLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Site.UPDATE_CREDIT_CARD_FEE_REQUEST));
  const { tipCalculatorPercentFee } = site?.settings || {};

  const initialValues = {
    percentFee: String(tipCalculatorPercentFee ?? 0),
  };

  const handleSubmit = (values: { percentFee: string }) => {
    dispatch(updateCreditCardFeeRequest(site.id, Number(values.percentFee), onClose));
  };

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
      {({ setFieldValue, dirty, isValid }) => (
        <Form data-cy="credit-card-fee-form">
          <Field name="percentFee">
            {({ field, meta }: FieldProps) => (
              <FieldWrapper
                label="CC Deduction Rate"
                id="credit-card-fee"
                tooltip="Value must be between 0% and 20%."
                tooltipIconSize={20}
                tooltipIconMarginLeft={theme.space.sm}
                data-cy="percent-fee"
                hasError={Boolean(meta.error)}
                message={meta.error || ''}
              >
                <InputIconWrapper icon="Percentage" iconPosition="right">
                  <NumberInput
                    {...field}
                    inputMode="decimal"
                    onChange={(e) => {
                      if (!feeInputPattern.test(e.target.value)) return;
                      setFieldValue('percentFee', e.target.value);
                    }}
                    $error={Boolean(meta.error)}
                    disabled={isUpdateLoading}
                    id="credit-card-fee-input"
                  />
                </InputIconWrapper>
              </FieldWrapper>
            )}
          </Field>

          <ActionSection $marginTop={0}>
            <Button label="Cancel" data-cy="cancel-button" appearance="white" size="small" onClick={onClose} disabled={isUpdateLoading} />
            <Button type="submit" label="Save" data-cy="save-credit-card-fee-button" size="small" isProcessing={isUpdateLoading} disabled={isUpdateLoading || !dirty || !isValid} />
          </ActionSection>
        </Form>
      )}
    </Formik>
  );
};
